/**
 * SelectStep - Company and Bank Account selection for Bank Entry workflow
 */

import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useBankEntryStore } from '../../stores/bankEntryStore';
import { Building2, Loader2, ArrowRight, ArrowLeft } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL;

export const SelectStep: React.FC = () => {
  console.log('[BankEntry] SelectStep RENDERING');
  const {
    setCurrentStep,
    selectedCompany,
    setSelectedCompany,
    selectedBankAccount,
    setSelectedBankAccount,
    companies,
    setCompanies,
    bankAccounts,
    setBankAccounts,
    isLoading,
    setLoading,
    setError,
    file,
    setRows,
    setStats,
    requiresPassword,
    filePassword,
    setFilePassword,
    setRequiresPassword,
  } = useBankEntryStore();
  const token = localStorage.getItem('auth_token');
  const { toast } = useToast();

  // Fetch companies on mount
  useEffect(() => {
    console.log('[BankEntry] SelectStep useEffect - token:', token ? 'present' : 'missing', 'API_BASE:', API_BASE);

    if (!token) {
      console.log('[BankEntry] No token, skipping fetch');
      return;
    }

    const fetchCompanies = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE}/api/bank-entry/companies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch companies');
        const data = await response.json();
        setCompanies(data || []);
      } catch (error) {
        console.error('[BankEntry] Error fetching companies:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch companies from Tally',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };
    fetchCompanies();
  }, [token, setCompanies, setLoading, toast]);

  // Fetch bank accounts when company changes
  useEffect(() => {
    if (!selectedCompany || !token) return;

    const fetchBankAccounts = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${API_BASE}/api/bank-entry/bank-accounts?company=${encodeURIComponent(selectedCompany)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error('Failed to fetch bank accounts');
        const data = await response.json();
        setBankAccounts(data || []);
      } catch (error) {
        console.error('Error fetching bank accounts:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch bank accounts',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };
    fetchBankAccounts();
  }, [selectedCompany, token, setBankAccounts, setLoading, toast]);

  const {
    setParseJobId,
    setParseJobStatus,
  } = useBankEntryStore();

  const handleContinue = async () => {
    if (!selectedCompany || !selectedBankAccount || !file) return;

    setLoading(true);
    setError(null);

    try {
      if (requiresPassword && !filePassword) {
        toast({
          title: 'Password required',
          description: 'Enter the statement password to continue.',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('company', selectedCompany);
      formData.append('bank_account', selectedBankAccount);
      if (requiresPassword) {
        formData.append('password', filePassword);
      }

      const response = await fetch(`${API_BASE}/api/bank-entry/parse`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        const detail = error?.detail;
        if (detail?.code === 'password_required') {
          setRequiresPassword(true);
          toast({
            title: 'Password required',
            description: detail?.message || 'Enter the statement password to continue.',
            variant: 'destructive',
          });
          return;
        }
        if (detail?.code === 'invalid_password') {
          setRequiresPassword(true);
          toast({
            title: 'Invalid password',
            description: detail?.message || 'The password is incorrect. Try again.',
            variant: 'destructive',
          });
          return;
        }
        throw new Error(detail?.message || 'Failed to submit parse job');
      }

      const data = await response.json();
      console.log('[BankEntry] Parse job submitted:', data);

      // Store job ID and move to parsing step
      setParseJobId(data.job_id);
      setParseJobStatus(0, 'Starting...');
      setRequiresPassword(false);
      setCurrentStep('parsing');
    } catch (error: any) {
      console.error('Error submitting parse job:', error);
      setRequiresPassword(false);
      setError(error.message);
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit parse job',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="w-5 h-5" />
          Select Company & Bank Account
        </CardTitle>
        <CardDescription>
          Choose the company and bank account for these entries
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <label className="text-sm font-medium">Company</label>
          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger>
              <SelectValue placeholder="Select company" />
            </SelectTrigger>
            <SelectContent>
              {companies.map((company) => (
                <SelectItem key={company} value={company}>
                  {company}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Bank Account</label>
          <Select
            value={selectedBankAccount}
            onValueChange={setSelectedBankAccount}
            disabled={!selectedCompany || bankAccounts.length === 0}
          >
            <SelectTrigger>
              <SelectValue placeholder={
                !selectedCompany
                  ? "Select company first"
                  : bankAccounts.length === 0
                    ? "No bank accounts found"
                    : "Select bank account"
              } />
            </SelectTrigger>
            <SelectContent>
              {bankAccounts.map((account) => (
                <SelectItem key={account.name} value={account.name}>
                  {account.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {requiresPassword && (
          <div className="space-y-2">
            <label className="text-sm font-medium">Statement Password</label>
            <Input
              type="password"
              value={filePassword}
              onChange={(event) => setFilePassword(event.target.value)}
              placeholder="Enter statement password"
              autoComplete="off"
            />
          </div>
        )}

        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => setCurrentStep('upload')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button
            onClick={handleContinue}
            disabled={!selectedCompany || !selectedBankAccount || isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Parse & Preview <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SelectStep;
